import React, {
  ElementType,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Error } from '@mui/icons-material';
import {
  Box,
  InputBaseComponentProps,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { InputProps as StandardInputProps } from '@mui/material/Input/Input';
import { unformat } from '@react-input/mask';
import Flag from 'country-flag-icons/react/3x2';

import { CountryCodeSelectionDialog } from '~components/atoms/custom/CountryCodeSelectionDialog';
import { PhoneInputMask } from '~components/atoms/custom/PhoneInputMask';
import { CountryItem } from '~configs';
import { useModal } from '~hooks/useModal';
import { useLanguageContext } from '~providers';
import { convertToNationalMask, isValidPhoneNumber } from '~utils';

type Props = {
  children?: React.ReactNode;
  phoneNumber: string;
  setPhoneNumber: React.Dispatch<React.SetStateAction<string>>;
  isValid: boolean | null;
  setIsValid: React.Dispatch<React.SetStateAction<boolean | null>>;
  defaultCountry: CountryItem;
  disabled?: boolean;
};

export const PhoneField: React.FC<Props> = ({
  phoneNumber,
  setPhoneNumber,
  isValid,
  setIsValid,
  defaultCountry,
  disabled = false,
}) => {
  const { lt } = useLanguageContext();
  const trackRawInputRef = useRef('');

  const [selectedCountry, setSelectedCountry] =
    useState<CountryItem>(defaultCountry);

  useEffect(() => {
    if (!trackRawInputRef.current) {
      setSelectedCountry(defaultCountry);
    }
  }, [defaultCountry]);

  const { isOpen, openModal, closeModal } = useModal();

  const options = useMemo(() => {
    return {
      mask: convertToNationalMask(selectedCountry),
      replacement: { '.': /\d/ },
    };
  }, [selectedCountry]);

  const flag = useMemo(() => {
    const countryCode = selectedCountry.iso2.toUpperCase() as keyof typeof Flag;
    const Comp = Flag[countryCode] || null;
    return Comp ? <Comp /> : null;
  }, [selectedCountry]);

  const handleCountryChange = (country: CountryItem): void => {
    setPhoneNumber('');
    trackRawInputRef.current = '';
    setIsValid(null);
    setSelectedCountry(country);
  };

  const onBlur = () => {
    setIsValid(isValidPhoneNumber(phoneNumber, selectedCountry.iso2));
  };

  const onChange: StandardInputProps['onChange'] = (event) => {
    if (!isValid) setIsValid(null);
    const value = event.target.value;
    const input = unformat(value, options);
    trackRawInputRef.current = input;
    const phoneNumber = '+' + selectedCountry.dialCode + input;
    setPhoneNumber(phoneNumber);
  };

  const hasError = isValid === false;
  const hasValue = !!trackRawInputRef.current;

  return (
    <>
      <TextField
        fullWidth
        onChange={onChange}
        onBlur={onBlur}
        type="tel"
        disabled={disabled}
        InputProps={{
          startAdornment: (
            <Box
              display={'inline-flex'}
              alignItems={'center'}
              onClick={disabled ? undefined : openModal}
              borderRight={1}
              borderColor="neutral.200"
              height={1}
              sx={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
            >
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                sx={{ paddingRight: 1.5 }}
              >
                <Box sx={{ width: 20 }}>{flag}</Box>
                <Typography noWrap variant="Inter/Body-sm">
                  +{selectedCountry.dialCode}
                </Typography>
              </Stack>
            </Box>
          ),
          // @see about the cast https://v5.mui.com/material-ui/react-text-field/#integration-with-3rd-party-input-libraries
          inputComponent:
            PhoneInputMask as unknown as ElementType<InputBaseComponentProps> & {
              hasError: boolean;
              hasValue: boolean;
            },
          inputProps: {
            hasValue,
            hasError,
            mask: options.mask,
            key: selectedCountry.iso2, //For updating value after change country
          },
          endAdornment: hasError ? (
            <Error color="error" fontSize="small" />
          ) : undefined,
        }}
        sx={{
          '& .MuiInputBase-root': { height: 56 },
          '& .MuiFormHelperText-root': {
            mt: 0.5,
            ml: 0,
          },
        }}
        error={hasError}
        helperText={
          hasError ? (
            <Typography variant="Inter/Body-sm">
              {lt('text.invalidPhoneNumber', 'Invalid phone number.')}
            </Typography>
          ) : null
        }
      />
      {isOpen && ( // Trick to improve performance
        <CountryCodeSelectionDialog
          currentValue={selectedCountry}
          open={isOpen}
          onClose={closeModal}
          onSelected={handleCountryChange}
        />
      )}
    </>
  );
};
